export interface LookDelta {
  x: number
  y: number
}

export class InputManager {
  private readonly held = new Set<string>()
  private readonly pressed = new Set<string>()
  private readonly mouseHeld = new Set<number>()
  private readonly mousePressed = new Set<number>()
  private lookX = 0
  private lookY = 0

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('blur', this.reset)
    document.addEventListener('mousemove', this.onMouseMove)
    canvas.addEventListener('mousedown', this.onMouseDown)
    window.addEventListener('mouseup', this.onMouseUp)
    canvas.addEventListener('contextmenu', this.preventContextMenu)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  isDown(code: string): boolean {
    return this.held.has(code)
  }

  wasPressed(code: string): boolean {
    if (!this.pressed.has(code)) return false
    this.pressed.delete(code)
    return true
  }

  isMouseDown(button: number): boolean {
    return this.mouseHeld.has(button)
  }

  wasMousePressed(button: number): boolean {
    if (!this.mousePressed.has(button)) return false
    this.mousePressed.delete(button)
    return true
  }

  consumeLook(): LookDelta {
    const result = { x: this.lookX, y: this.lookY }
    this.lookX = 0
    this.lookY = 0
    return result
  }

  clearTransient(): void {
    this.pressed.clear()
    this.mousePressed.clear()
    this.lookX = 0
    this.lookY = 0
  }

  resetAll(): void {
    this.reset()
  }

  requestPointerLock(): void {
    if (navigator.webdriver) return
    if (document.pointerLockElement !== this.canvas) {
      void this.canvas.requestPointerLock().catch(() => undefined)
    }
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('blur', this.reset)
    document.removeEventListener('mousemove', this.onMouseMove)
    this.canvas.removeEventListener('mousedown', this.onMouseDown)
    window.removeEventListener('mouseup', this.onMouseUp)
    this.canvas.removeEventListener('contextmenu', this.preventContextMenu)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isInteractiveTarget(event.target)) return
    if (!this.held.has(event.code)) this.pressed.add(event.code)
    this.held.add(event.code)
    if (['Space', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
      event.preventDefault()
    }
  }

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code)
  }

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (document.pointerLockElement === this.canvas) {
      this.lookX += event.movementX
      this.lookY += event.movementY
    }
  }

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (!this.mouseHeld.has(event.button)) this.mousePressed.add(event.button)
    this.mouseHeld.add(event.button)
  }

  private readonly onMouseUp = (event: MouseEvent): void => {
    this.mouseHeld.delete(event.button)
  }

  private readonly preventContextMenu = (event: MouseEvent): void => {
    event.preventDefault()
  }

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.reset()
  }

  private readonly reset = (): void => {
    this.held.clear()
    this.pressed.clear()
    this.mouseHeld.clear()
    this.mousePressed.clear()
    this.lookX = 0
    this.lookY = 0
  }
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('button, a[href], input, select, textarea, [role="button"]') !== null
  )
}
